import { useContext, useState } from 'react';
import {
  Block,
  Button,
  FlexContainer,
  InputText,
  Modal,
  SoundNotificationContext,
  Span,
  useNotificationsV2,
} from '@uni-space/uni-components';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getErrorMessageAsync } from '@shared/api';
import { sendParcelDimensions } from '../api';
import { emptyDimensionsDraft, isValidDimensionsDraft, toParcelDimensions } from '../model/dimensions';
import DimensionsFields from './DimensionsFields';

export default function ChangeDimensionsModal({
  isOpen,
  onChangeOpen,
}: {
  isOpen: boolean;
  onChangeOpen: (isOpen: boolean) => void;
}) {
  const [trackNumber, setTrackNumber] = useState('');
  const [draft, setDraft] = useState(emptyDimensionsDraft);
  const { createNotification } = useNotificationsV2();
  const { onSoundSuccess, onSoundError } = useContext(SoundNotificationContext);
  const { t } = useTranslation();
  const valid = Boolean(trackNumber.trim()) && isValidDimensionsDraft(draft);

  const close = () => {
    onChangeOpen(false);
    setTrackNumber('');
    setDraft(emptyDimensionsDraft);
  };

  const { mutate: changeDimensions, isPending } = useMutation({
    mutationKey: ['changeParcelDimensions'],
    mutationFn: () => sendParcelDimensions({ trackNumber: trackNumber.trim(), dimensions: toParcelDimensions(draft) }),
    onSuccess: () => {
      createNotification({ type: 'success', message: { title: t('consolidatorTracking.changeDimensions.success') } });
      onSoundSuccess();
      close();
    },
    onError: async (e) => {
      const message = await getErrorMessageAsync(e, t('common.systemError.desc'));
      createNotification({ type: 'error', message: { title: t('common.systemError.title'), desc: message } });
      onSoundError();
    },
  });

  const submit = () => {
    if (valid && !isPending) changeDimensions();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isPending) close();
      }}
      width={{ min: '280px', max: '560px' }}
    >
      <FlexContainer direction="column" gap="16px">
        <Block textAlign="center" mb={8}>
          <Span fs={24}>{t('consolidatorTracking.changeDimensions.title')}</Span>
        </Block>
        <InputText
          id="change-dimensions-track-number"
          label={t('trackNumberLabel')}
          autoComplete="off"
          disabled={isPending}
          value={trackNumber}
          onChange={setTrackNumber}
        />
        <DimensionsFields
          idPrefix="change-dimensions"
          value={draft}
          disabled={isPending}
          onChange={setDraft}
          onSubmit={submit}
        />
        <Button disabled={!valid} loading={isPending} onClick={submit}>
          {t('send')}
        </Button>
      </FlexContainer>
    </Modal>
  );
}
